/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useMemo, useState } from 'react'
import { Box, Checkbox, Link, Radio, Typography } from '@mui/material'

import { ClientStudentInstance } from 'common/utils/types/account-types-client'
import useCurrentTeacher from 'common/hooks/useCurrentTeacher'
import PremiumIcon from 'assets/teacher/premium.svg'

import {
  DEFAULT_PROGRAM_SETTINGS,
  DIFFICULTY_LEVELS,
  DIFFICULTY_LEVELS_LABELS,
  OPERATIONS,
  PROGRAM_LENGTHS,
  TIMER_THRESHOLDS,
} from '../constants'
import InfoIconWithPopover from './InfoIconWithPopover'

const OPERATION_TOOLTIP = {
  premium: (
    <Typography variant='body1'>
      Choose one or more skills for your student to practice. With XtraMath
      Premium, you get full access to all settings and program options.{' '}
      <Link
        href='https://home.xtramath.org/support/suggested-programs'
        target='_blank'
        rel='noopener'
      >
        Learn more.
      </Link>
    </Typography>
  ),
  free: (
    <Typography variant='body1'>
      Choose one or more skills for your student to practice. With the free
      plan, previously selected operations are automatically preselected in
      order.{' '}
      <Link
        href='https://home.xtramath.org/faq-categories/student-program-adjustments'
        target='_blank'
        rel='noopener'
      >
        Learn more
      </Link>
    </Typography>
  ),
}

const PREMIUM_TIMER_THRESHOLDS = { 2: true, '1.5': true }
const PREMIUM_DIFFICULTY_LEVELS = { extended: true }
const PREMIUM_PROGRAM_LENGTHS = { fluencyAssessment: true }

function SelectProgram({
  selectedStudents,
  selectedProgram,
  setSelectedProgram,
}: {
  selectedStudents: ClientStudentInstance[]
  selectedProgram: typeof DEFAULT_PROGRAM_SETTINGS
  setSelectedProgram: (program: typeof DEFAULT_PROGRAM_SETTINGS) => void
}) {
  const { currentTeacher } = useCurrentTeacher()
  const isPremium = useMemo(
    () => currentTeacher?.hasActiveLicense(),
    [currentTeacher]
  )
  const [hoveredOperation, setHoveredOperation] = useState<
    (typeof OPERATIONS)[number] | null
  >(null)

  const getHoverPreview = (
    operation: (typeof OPERATIONS)[number]
  ): Record<string, boolean> => {
    if (isPremium) {
      return {}
    }

    const isCurrentlySelected = selectedProgram.operation[operation]
    if (isCurrentlySelected) {
      return {}
    }

    const operationIndex = OPERATIONS.indexOf(operation)
    const preview: Record<string, boolean> = {}

    for (let i = 0; i <= operationIndex; i++) {
      preview[OPERATIONS[i]] = true
    }

    return preview
  }

  const handleChangeOperation = (operation: (typeof OPERATIONS)[number]) => {
    if (isPremium) {
      setSelectedProgram({
        ...selectedProgram,
        operation: {
          ...selectedProgram.operation,
          [operation]: !selectedProgram.operation[operation],
        },
      })
      return
    }

    const operationIndex = OPERATIONS.indexOf(operation)
    const isCurrentlySelected = selectedProgram.operation[operation]
    const newOperation = { ...selectedProgram.operation }

    if (isCurrentlySelected) {
      for (let i = operationIndex; i < OPERATIONS.length; i++) {
        newOperation[OPERATIONS[i]] = false
      }
    } else {
      for (let i = 0; i <= operationIndex; i++) {
        newOperation[OPERATIONS[i]] = true
      }
    }

    setSelectedProgram({
      ...selectedProgram,
      operation: newOperation,
    })
  }

  return (
    <Box>
      <Typography variant='body1' sx={{ p: 2 }}>
        You are assigning a new program to{' '}
        <Box
          sx={{
            display: 'inline-block',
            textDecoration: 'underline',
          }}
        >
          {selectedStudents.length} student
          {selectedStudents.length > 1 ? 's' : ''}
        </Box>
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>Operation</Typography>
            <InfoIconWithPopover
              tooltip={
                isPremium ? OPERATION_TOOLTIP.premium : OPERATION_TOOLTIP.free
              }
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '48px' }}>
            {OPERATIONS.map((operation) => {
              const hoverPreview = hoveredOperation
                ? getHoverPreview(hoveredOperation)
                : {}
              const isHoverPreview =
                hoveredOperation !== null &&
                hoverPreview[operation] !== undefined
              const wouldBeSelected = hoverPreview[operation] === true
              const wouldBeDeselected = hoverPreview[operation] === false

              return (
                <Box
                  key={operation}
                  component='label'
                  onMouseEnter={() => setHoveredOperation(operation)}
                  onMouseLeave={() => setHoveredOperation(null)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    opacity:
                      isHoverPreview && wouldBeDeselected ? 0.5 : undefined,
                    backgroundColor:
                      isHoverPreview && wouldBeSelected
                        ? 'rgba(255, 198, 65, 0.1)'
                        : 'transparent',
                    borderRadius: 1,
                    py: isHoverPreview && wouldBeSelected ? 0.5 : 0,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Checkbox
                    color='secondary'
                    checked={selectedProgram.operation[operation]}
                    onChange={() => handleChangeOperation(operation)}
                    sx={{
                      opacity:
                        isHoverPreview && wouldBeDeselected ? 0.5 : undefined,
                    }}
                  />
                  <Typography
                    variant='body1'
                    sx={{
                      textTransform: 'capitalize',
                      opacity:
                        isHoverPreview && wouldBeDeselected ? 0.5 : undefined,
                    }}
                  >
                    {operation}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>Timer Threshold</Typography>
            <InfoIconWithPopover
              tooltip={
                <Typography variant='body1'>
                  Set the time limit for each answer. A shorter threshold helps
                  build fluency, while a longer time gives students more time to
                  think.{' '}
                  <Link
                    href='https://home.xtramath.org/support/what-are-the-timer-options-and-how-do-they-benefit-students'
                    target='_blank'
                    rel='noopener'
                  >
                    Learn&nbsp;more
                  </Link>
                </Typography>
              }
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '48px' }}>
            {TIMER_THRESHOLDS.map((timerThreshold) => (
              <Box
                component='label'
                key={timerThreshold}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {!isPremium && PREMIUM_TIMER_THRESHOLDS[timerThreshold] && (
                  <Box
                    component='img'
                    src={PremiumIcon}
                    alt='Premium'
                    sx={{ position: 'absolute', right: -30, top: -5 }}
                  />
                )}
                <Radio
                  color='secondary'
                  checked={selectedProgram.timerThreshold === timerThreshold}
                  disabled={
                    !isPremium && PREMIUM_TIMER_THRESHOLDS[timerThreshold]
                  }
                  onChange={() => {
                    setSelectedProgram({
                      ...selectedProgram,
                      timerThreshold,
                    })
                  }}
                />
                <Typography
                  variant='body1'
                  sx={{
                    opacity:
                      !isPremium && PREMIUM_TIMER_THRESHOLDS[timerThreshold]
                        ? 0.5
                        : 1,
                  }}
                >
                  {timerThreshold}s
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>Difficulty Level</Typography>
            <InfoIconWithPopover
              tooltip={
                <Typography variant='body1'>
                  Select the starting difficulty. The level is determined by the
                  size of the matrix and the range of number included.
                </Typography>
              }
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '48px' }}>
            {DIFFICULTY_LEVELS.map((difficultyLevel) => (
              <Box
                component='label'
                key={difficultyLevel}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {!isPremium && PREMIUM_DIFFICULTY_LEVELS[difficultyLevel] && (
                  <Box
                    component='img'
                    src={PremiumIcon}
                    alt='Premium'
                    sx={{ position: 'absolute', right: -30, top: -5 }}
                  />
                )}
                <Radio
                  color='secondary'
                  checked={selectedProgram.difficultyLevel === difficultyLevel}
                  disabled={
                    !isPremium && PREMIUM_DIFFICULTY_LEVELS[difficultyLevel]
                  }
                  onChange={() => {
                    setSelectedProgram({
                      ...selectedProgram,
                      difficultyLevel,
                    })
                  }}
                />
                <Typography
                  variant='body1'
                  sx={{
                    opacity:
                      !isPremium && PREMIUM_DIFFICULTY_LEVELS[difficultyLevel]
                        ? 0.5
                        : 1,
                  }}
                >
                  {DIFFICULTY_LEVELS_LABELS[difficultyLevel]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>Program Length</Typography>
            <InfoIconWithPopover
              tooltip={
                <Typography variant='body1'>
                  Choose how your student will practice:{' '}
                  <b>Adaptive Practice</b> includes quizzes followed by
                  personalized practice, while <b>Placement Quiz</b> checks the
                  student’s current level without additional practice.
                </Typography>
              }
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '48px' }}>
            {Object.keys(PROGRAM_LENGTHS).map((programLength) => (
              <Box
                component='label'
                key={PROGRAM_LENGTHS[programLength]}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {!isPremium && PREMIUM_PROGRAM_LENGTHS[programLength] && (
                  <Box
                    component='img'
                    src={PremiumIcon}
                    alt='Premium'
                    sx={{ position: 'absolute', right: -30, top: -5 }}
                  />
                )}
                <Radio
                  color='secondary'
                  checked={selectedProgram.programLength === programLength}
                  disabled={
                    !isPremium && PREMIUM_PROGRAM_LENGTHS[programLength]
                  }
                  onChange={() => {
                    setSelectedProgram({
                      ...selectedProgram,
                      programLength,
                    })
                  }}
                />
                <Typography
                  variant='body1'
                  sx={{
                    opacity:
                      !isPremium && PREMIUM_PROGRAM_LENGTHS[programLength]
                        ? 0.5
                        : 1,
                  }}
                >
                  {PROGRAM_LENGTHS[programLength]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {!isPremium && (
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <img src={PremiumIcon} alt='Premium' />
          <Typography
            variant='body2'
            component='a'
            target='parent'
            href='/#/teacher/upgrade_license'
            sx={{ color: '#007AAB' }}
          >
            Unlock All Options with <strong>Xtramath Premium</strong>
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default SelectProgram
